import {
  useDispatchGetCompatibilityReport,
  useIsLoading,
  useIsSub,
  useMemberAttribute,
  useSelectAccountAttributes,
} from 'affiny-core/hooks';
import { ECompatibilityReportType } from 'affiny-core/modules/api/models/member/member.types';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { compatibilityReportActions } from 'affiny-core/modules/compatibilityReport/actions';
import { Container } from 'affiny-desktop/src/components/desktop/Container';
import { Loader } from 'affiny-ui/src';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { animateScroll } from 'react-scroll';
import { CompatibilityReportBreaker } from './CompatibilityReportBreaker';
import { CompatibilityReportPersonalitiesChapter } from './CompatibilityReportPersonalitiesChapter';
import { CompatibilityReportValuesChapter } from './CompatibilityReportValuesChapter';
import { CompatibilityReportVisionChapter } from './CompatibilityReportVisionChapter';
import { Tabs } from '../Tabs';

const CompatibilityReportLayer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const accountId = new URLSearchParams(window.location.search).get(
    'id'
  ) as string;

  useDispatchGetCompatibilityReport(accountId, ECompatibilityReportType.VALUE);

  const isSub = useIsSub();
  const attributes = useSelectAccountAttributes();
  const memberNickname = useMemberAttribute('nickname', accountId);
  const valueScore = useMemberAttribute('valueScore', accountId);
  const visionScore = useMemberAttribute('visionScore', accountId);
  const personalityScore = useMemberAttribute('personalityScore', accountId);

  const isLoadingCompatibilityReport = useIsLoading(
    EAsyncEventTypes.GET_COMPATIBILITY_REPORT
  );

  useEffect(() => {
    dispatch(
      compatibilityReportActions.getCompatibilityReport({
        accountId,
        reportType: ECompatibilityReportType.VALUE,
      })
    );
  }, [accountId, dispatch]);

  const sections = useMemo(
    () => [
      {
        label: 'values',
        title: t('compatibilityReport.tabs.values'),
      },
      {
        label: 'visions',
        title: t('compatibilityReport.tabs.visions'),
      },
      {
        label: 'personalities',
        title: t('compatibilityReport.tabs.personalities'),
      },
    ],
    [t]
  );

  const [section, setSection] = useState(sections[0].label);

  const goToNextSection = (label: string) => {
    animateScroll.scrollTo(0);
    setSection(label);
  };

  const renderSection = useCallback(() => {
    const wantedGender = attributes?.wantedGender;

    const commonTabProps = {
      nickname: memberNickname,
      accountId,
      wantedGender,
      setSection: goToNextSection,
    };

    switch (section) {
      case 'visions':
        return (
          <CompatibilityReportVisionChapter
            {...commonTabProps}
            reportType={ECompatibilityReportType.VISION}
            score={visionScore}
          />
        );
      case 'personalities':
        return (
          <CompatibilityReportPersonalitiesChapter
            {...commonTabProps}
            score={personalityScore}
            reportType={ECompatibilityReportType.PERSONALITY}
          />
        );
      default:
        return (
          <CompatibilityReportValuesChapter
            {...commonTabProps}
            score={valueScore}
            reportType={ECompatibilityReportType.VALUE}
          />
        );
    }
  }, [
    accountId,
    attributes,
    personalityScore,
    valueScore,
    section,
    memberNickname,
    visionScore,
  ]);

  if (!isSub) document.body.style.overflowY = 'hidden';

  return (
    <Container>
      <Loader isLoading={isLoadingCompatibilityReport} />
      <Tabs
        sections={sections}
        section={section}
        setSection={goToNextSection}
        margin={24}
      />
      {renderSection()}

      {!isSub && <CompatibilityReportBreaker />}
    </Container>
  );
};

export default CompatibilityReportLayer;
